import Modal from "@ui-components/Modal";
import { useState, useEffect } from "react";
import { EstimeDatesModalProps } from "./types";
import DatePicker from "ui-components/DatePicker";
import { FormattedMessage, useIntl } from "react-intl";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import Button from "@ui-components/Button";

export const EstimeDatesModal = ({
  setOpenModal,
  selectedWorksite,
  setSelectedWorksite,
  text,
  cancelText,
  onClose,
}: EstimeDatesModalProps) => {
  const [estimeDateStart, setEstimeDateStart] = useState<Date>();
  const [estimeDateEnd, setEstimeDateEnd] = useState<Date>();
  const [loading, setLoading] = useState(false);

  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();

  let [estimeDateStartWarning, setEstimeDateStartWarning] = useState(false);
  let [estimeDateEndWarning, setEstimeDateEndWarning] = useState(false);

  useEffect(() => {
    if (selectedWorksite.estime_date_start) {
      setEstimeDateStart(new Date(selectedWorksite.estime_date_start));
    }
    if (selectedWorksite.estime_date_end) {
      setEstimeDateEnd(new Date(selectedWorksite.estime_date_end));
    }
  }, []);

  useEffect(() => {
    setEstimeDateStartWarning(false);
    setEstimeDateEndWarning(false);

    let statusList = [];
    for (let el in selectedWorksite.cod_worksite_status) {
      let step = selectedWorksite.cod_worksite_status[el];
      if (step.flg_active) {
        statusList.push(step.step);
      }
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isStartDatePassed = today >= estimeDateStart;
    const isEndDatePassed = today >= estimeDateEnd;
    // Data di inizio passata e stato non in esecuzione
    if (
      estimeDateStart &&
      statusList.findIndex((el) => el === "open") === -1 &&
      isStartDatePassed
    ) {
      setEstimeDateStartWarning(true);
    }
    // Data di fine passata e stato non in chiusura

    if (
      estimeDateEnd &&
      statusList.findIndex((el) => el === "closed") === -1 &&
      isEndDatePassed
    ) {
      setEstimeDateEndWarning(true);
    }
  }, [estimeDateEnd, estimeDateStart]);

  const _onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Send dates and worksite id to the backend
    let _selectedWorksite = { ...selectedWorksite };
    let data = await countryApiRequest.put(
      `/estime_dates/${_selectedWorksite.id}`,
      { start: estimeDateStart, end: estimeDateEnd }
    );
    setSelectedWorksite(data);
    setLoading(false);
    setOpenModal(false);
  };

  return (
    <Modal
      onExit={() => {
        onClose ? onClose() : setOpenModal(false);
      }}
      submitting={loading}
    >
      <div className="mb-8">
        <h3 className="mb-4 text-center text-2xl font-extrabold text-black-500">
          <FormattedMessage
            id="edit_estime_dates_title"
            defaultMessage="Data esecuzione"
          />
        </h3>
        <h3 className="mb-8 text-xl font-bold text-black-500">
          {text ? (
            <span>{text}</span>
          ) : (
            <FormattedMessage
              id="edit_estime_dates_subtitle"
              defaultMessage="inserisci le date di inizio e fine dell'esecuzione del cantiere"
            />
          )}
        </h3>
        <h4 className="font-bold text-gray-500">
          <FormattedMessage
            id="edit_estime_dates_subtitle_info"
            defaultMessage="le date potranno essere modificate in seguito"
          />
        </h4>
      </div>
      <form onSubmit={_onSubmit}>
        <div className="mt-3 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-2 sm:grid-flow-row-dense">
          <div className="basis-1/2 w-full">
            <DatePicker
              required
              value={estimeDateStart}
              minDate={null}
              maxDate={estimeDateEnd ? estimeDateEnd : null}
              label={intl.formatMessage({
                id: "start",
                defaultMessage: "Inizio",
              })}
              placeholder={intl.formatMessage({
                id: "select_date",
                defaultMessage: "Seleziona la data",
              })}
              position={"relative"}
              onChange={(value: Date) => {
                if (value) value.setHours(0, 0, 0, 0);
                setEstimeDateStart(value);
              }}
              {...(estimeDateStartWarning
                ? { inputTextColor: "text-red-500" }
                : {})}
              validateOnBlur={false}
            />
          </div>
          <div className="basis-1/2 ">
            <DatePicker
              required
              value={estimeDateEnd}
              minDate={estimeDateStart ? estimeDateStart : null}
              label={intl.formatMessage({ id: "end" })}
              placeholder={intl.formatMessage({
                id: "select_date",
                defaultMessage: "Seleziona la data",
              })}
              //zIndexVal={2}
              position={"relative"}
              onChange={(value: Date) => {
                if (value) value.setHours(0, 0, 0, 0);
                setEstimeDateEnd(value);
              }}
              {...(estimeDateEndWarning
                ? { inputTextColor: "text-red-500" }
                : {})}
              validateOnBlur={false}
            />
          </div>
        </div>
        {estimeDateStartWarning && (
          <div className="mt-2 text-red-500">
            <FormattedMessage
              id="estime_date_start_warning"
              defaultMessage="*Attenzione: la data di inizio esecuzione è passata. Aggiornare la data o lo stato del cantiere."
            />
          </div>
        )}
        {estimeDateEndWarning && (
          <div className="mt-2 text-red-500">
            <FormattedMessage
              id="estime_date_end_warning"
              defaultMessage="*Attenzione: la data di fine esecuzione è passata. Aggiornare la data o lo stato del cantiere."
            />
          </div>
        )}
        <div className="grid mt-4 gap-y-3">
          <Button
            submitting={loading}
            type="submit"
            className="relative overflow-hidden uppercase justify-center inline-flex items-center px-4 pb-2 pt-1.5 border border-gray-300 text-base font-semibold focus:outline-none active:opacity-70 text-white bg-black-500 border-black-500 hover:bg-am-700  w-full"
          >
            <FormattedMessage id="save" />
          </Button>
          <Button
            onClick={() => {
              setOpenModal(false);
            }}
            styleType={"white"}
            className="relative overflow-hidden uppercase justify-center inline-flex items-center px-4 pb-2 pt-1.5 border border-gray-300 text-base font-semibold focus:outline-none active:opacity-70 text-white bg-black-500 border-black-500 hover:bg-am-700  w-full"
          >
            {cancelText ? (
              <span>{cancelText}</span>
            ) : (
              <FormattedMessage id="cancel" defaultMessage="Annulla" />
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
